import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useMainContext } from '../../hooks/useMainContext';
import { Dropdown, message, Tooltip } from 'antd';
import { CanvasUtils } from '../../utils/CanvasUtils';
import LengthInputForm from './LengthInputForm';
import { autorun } from 'mobx';
import {
    DEFAULT_PAGE_SCALE,
    EXTRACT_TEXT_HEIGHT_PERCENTAGE,
    EXTRACT_TEXT_WIDTH_PERCENTAGE,
    ScaleSource,
} from '../../constant';
import {
    ExclamationCircleOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';
import { Utils } from '../../utils/Utils';
import Button from '../Globle/Button';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import { CalibrateIcon, ExtractIcon } from '../Globle/Icon';

const ScaleInput = observer(() => {
    const { closetManager, drawingManager, viewManager } = useMainContext();
    const [scaleValue, setScaleValue] = useState(DEFAULT_PAGE_SCALE);
    const [isCalibrating, setIsCalibrating] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    // Check if user is super admin
    const isDisabled = !viewManager.doUserHasAccess();
    useEffect(() => {
        const dispose = autorun(() => {
            const newScale = closetManager.getScaleForPage(
                closetManager.selectedPageNo,
            );
            if (!newScale) {
                setScaleValue(DEFAULT_PAGE_SCALE);
                return;
            }
            setScaleValue(newScale);
        });

        return () => dispose();
    }, []);
    const handleCalibrate = () => {
        if (isDisabled) return;
        drawingManager.handleDeselectAll();
        if (closetManager.canCalibrate) {
            closetManager.setCanCalibrate(false);
            drawingManager.setCalibrationMode(false);
            setIsCalibrating(false);
            closetManager.setCalibrationData([]);
            return;
        }
        closetManager.setCanCalibrate(true);
        if (!closetManager.canCalibrate) return;
        setIsCalibrating(true);
        drawingManager.setCalibrationMode(true);
        message.info('Draw the calibration line.');
    };

    const handleSetLength = (feetNum, inchesNum) => {
        if (isDisabled) return;
        const p1 = closetManager.calibrationData[0];
        const p2 = closetManager.calibrationData[1];

        const pdfScale = closetManager.fetchTransformationScaleFactorByPageNo(
            closetManager.selectedPageNo,
        );
        const pageScale = closetManager.getScaleForPage(
            closetManager.selectedPageNo,
        );
        const newScale = CanvasUtils.calibratingScale(
            p1,
            p2,
            Number(feetNum),
            Number(inchesNum),
            pdfScale * pageScale,
        );
        if (!newScale) {
            closetManager.setCanCalibrate(false);
            closetManager.setCalibrationData([]);
            setIsCalibrating(false);
            closetManager.drawNewClosets();
            message.error('Calibration distance must be greater than zero.');
            return;
        }
        setScaleValue(pageScale / newScale);

        closetManager.setPageScale(
            closetManager.selectedPageNo,
            pageScale / newScale,
            ScaleSource.CALIBRATED,
        );
        closetManager.setUnitsScaleForSelectedPage(pageScale / newScale);
        closetManager.setCanCalibrate(false);
        closetManager.setCalibrationData([]);
        setIsCalibrating(false);
        closetManager.drawNewClosets();
        message.success(`Set length to ${feetNum} ft ${inchesNum} in`);
    };

    const handleExtractScale = async () => {
        if (isDisabled) return;
        drawingManager.handleDeselectAll();
        const page = await closetManager.pdfDoc.getPage(
            closetManager.selectedPageNo,
        );
        const newScale = await Utils.extractTextFromCustomBox(
            EXTRACT_TEXT_WIDTH_PERCENTAGE,
            EXTRACT_TEXT_HEIGHT_PERCENTAGE,
            page,
        );
        if (!newScale) return;
        setScaleValue(newScale);
        closetManager.setPageScale(
            closetManager.selectedPageNo,
            newScale,
            ScaleSource.EXTRACTED,
        );
        closetManager.setUnitsScaleForSelectedPage(newScale);
        closetManager.drawNewClosets();
    };
    const scaleButtons = [
        {
            label: 'Calibrate',
            onClick: handleCalibrate,
            disabled: isDisabled,
            icon: <CalibrateIcon />,
        },
        {
            label: 'Extract',
            onClick: handleExtractScale,
            disabled: isDisabled,
            icon: <ExtractIcon />,
        },
    ];
    const renderMenu = () => {
        return (
            <div className="p-[8px] bg-theme-white w-[134px] shadow-card rounded-[8px]">
                {scaleButtons.map((button, index) => (
                    <button
                        key={index}
                        onClick={() => {
                            setIsOpen(false);
                            button.onClick();
                        }}
                        disabled={button.disabled}
                        className="flex items-center text-[14px] font-semibold scale-button gap-[8px] w-full hover:bg-theme-button-bg  p-[8px] rounded-[8px] transition-all duration-300 ease-in-out">
                        {button.icon}
                        {button.label}
                    </button>
                ))}
            </div>
        );
    };

    return (
        <div className="flex items-start gap-[8px] items-center">
            <div className="flex gap-[4px] items-center">
                <label
                    htmlFor="fraction"
                    className="font-favorit text-[14px] leading-[14px] font-[700] text-theme-black">
                    Scale
                </label>
                {closetManager.getScaleTypeForPage(
                    closetManager.selectedPageNo,
                ) === ScaleSource.DEFAULT ? (
                    <Tooltip title="Scale not found" placement="top">
                        <ExclamationCircleOutlined
                            style={{ color: '#F46147', fontSize: '18px' }}
                        />
                    </Tooltip>
                ) : (
                    <Tooltip
                        title={`Scale Type: ${
                            ScaleSource[
                                closetManager.getScaleTypeForPage(
                                    closetManager.selectedPageNo,
                                )
                            ] ?? 'Unknown'
                        }`}
                        placement="bottom">
                        <InfoCircleOutlined
                            style={{
                                color: '#888',
                                fontSize: '18px',
                                marginLeft: 4,
                            }}
                        />
                    </Tooltip>
                )}
            </div>
            <div className="flex gap-[8px] flex-col">
                <div className="flex gap-[8px] items-center">
                    <input
                        disabled={isDisabled}
                        type="text"
                        value={scaleValue} // Bind to local state
                        onChange={(e) => setScaleValue(e.target.value)} // Update local state
                        className={`fraction-input h-[32px] w-[120px] bg-theme-header-bg text-center  p-[8px] rounded-[8px] focus:outline-none focus:border-theme-button-bg text-[#000000] ${
                            isDisabled
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed border-gray-300'
                                : ''
                        }`}
                    />

                    <Button
                        disabled={isDisabled}
                        onClick={() => {
                            if (isDisabled) return;
                            drawingManager.handleDeselectAll();
                            const parsedValue = Number(scaleValue);
                            if (isNaN(parsedValue) || parsedValue <= 0) {
                                message.error(
                                    'Scale must be a positive number',
                                );
                                setScaleValue(
                                    closetManager.getScaleForPage(
                                        closetManager.selectedPageNo,
                                    ),
                                );
                                return;
                            }
                            closetManager.setPageScale(
                                closetManager.selectedPageNo,
                                parsedValue,
                                ScaleSource.MANUAL,
                            );
                            closetManager.setUnitsScaleForSelectedPage(
                                parsedValue,
                            );
                            closetManager.drawNewClosets();
                        }}
                        label={'Update'}
                        className="!w-[95px] !h-[32px] !py-[8px] rounded-[8px] flex items-center justify-center bg-theme-header-bg text-[16px] font-[700] hover:!bg-theme-button-bg hover:!text-black"
                    />
                    <Dropdown
                        open={isOpen}
                        placement="bottomRight"
                        onOpenChange={setIsOpen}
                        dropdownRender={renderMenu}
                        trigger={['click']}>
                        <div
                            className={`flex items-center gap-[8px] cursor-pointer h-[32px] pl-[16px] pr-[8px] rounded-[50px] ${isDisabled ? 'pointer-events-none  bg-theme-header-bg  text-theme-button-bg' : isOpen ? 'text-theme-white bg-theme-black' : 'text-theme-black bg-theme-header-bg hover:bg-theme-button-bg'}`}>
                            <span
                                className={`text-[16px] font-favorit ${isOpen ? 'font-[500] text-theme-white' : ''}`}>
                                Adjust
                            </span>
                            {isOpen ? (
                                <IoIosArrowUp size={20} />
                            ) : (
                                <IoIosArrowDown size={20} />
                            )}
                        </div>
                    </Dropdown>
                </div>
                <LengthInputForm
                    visible={isCalibrating && !isDisabled}
                    onSubmit={handleSetLength}
                    onCancel={() => {
                        setIsCalibrating(false);
                        closetManager.setCanCalibrate(false);
                        drawingManager.setCalibrationMode(false);
                    }}
                />
            </div>
        </div>
    );
});

export default ScaleInput;
